<!-- eslint-disable vue/no-v-html -->
<template>
  <div
    class="reward-tooltip"
    v-bind="$attrs"
    v-on="$listeners"
    @click="handleClick"
  >
    <slot></slot>
    <div
      v-if="show"
      ref="tooltip"
      class="reward-tooltip__detail"
      :class="popoverClass"
    >
      <div class="reward-tooltip__detail-inner">
        <slot name="detail">
          <div
            v-if="name"
            class="reward-tooltip__name"
            v-html="name"
          ></div>
          <div
            v-if="desc"
            class="reward-tooltip__desc"
            v-html="desc"
          ></div>
        </slot>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'RewardTooltip',
  inheritAttrs: false,
  props: {
    allowClick: {
      type: Boolean,
      default: true,
    },
    name: {
      type: String,
      default: '',
    },
    desc: {
      type: String,
      default: '',
    },
    trigger: {
      type: String,
      default: 'click',
    },
    getContainer: {
      type: Function,
      default: () => document.body,
    },
    popoverClass: {
      type: String,
      default: '',
    },
    placement: {
      type: String,
      default: 'top',
      validator: val => ['top', 'bottom', 'left', 'right'].includes(val),
    },
  },
  data() {
    return {
      show: false,
      lastTriggerEl: null,
      viewportChangeHandler: null,
      destroyOnScroll: null,
      tooltipAppended: false,
      resizeObserver: null,
      scrollableContainers: [],
    }
  },
  mounted() {
    // 统一的滚动处理：展开时关闭 tooltip
    this.destroyOnScroll = this.throthle(() => {
      if (this.show) {
        this.destroyTooltip()
      }
    }, 80)
    window.addEventListener('scroll', this.destroyOnScroll, { passive: true })

    // 查找所有可能的滚动容器并添加监听
    this.scrollableContainers = Array.from(document.querySelectorAll('div'))
    this.scrollableContainers.forEach(container => {
      container.addEventListener('scroll', this.destroyOnScroll, { passive: true })
      container.addEventListener('touchmove', this.destroyOnScroll, { passive: true }) // 添加 passive 选项，因为这里不需要阻止默认行为
    })

    // 监听视口变化（iOS Safari 地址栏收起/展开会触发）
    this.viewportChangeHandler = this.throthle(() => {
      if (this.show) {
        this.positionTooltip()
      }
    }, 50)
    window.addEventListener('resize', this.viewportChangeHandler)
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', this.viewportChangeHandler)
      window.visualViewport.addEventListener('scroll', this.viewportChangeHandler)
    }
  },
  destroyed() {
    document.removeEventListener('click', this.handleOutsideClick)
    window.removeEventListener('scroll', this.destroyOnScroll)
    window.removeEventListener('resize', this.viewportChangeHandler)
    if (window.visualViewport) {
      window.visualViewport.removeEventListener('resize', this.viewportChangeHandler)
      window.visualViewport.removeEventListener('scroll', this.viewportChangeHandler)
    }
    this.scrollableContainers.forEach(container => {
      container.removeEventListener('scroll', this.destroyOnScroll)
      container.removeEventListener('touchmove', this.destroyOnScroll)
    })
    if (this.resizeObserver) this.resizeObserver.disconnect()
  },
  methods: {
    handleOutsideClick(e) {
      // 检查点击是否在tooltip内部
      const tooltip = this.$refs.tooltip

      if (tooltip && !tooltip.contains(e.target) && !this.$el.contains(e.target)) {
        this.destroyTooltip()
      }
    },
    throthle(fn, delay) {
      let last = 0
      return function(...args) {
        const now = new Date().getTime()
        if (now - last < delay) {
          return
        }
        last = now
        fn(...args)
      }
    },
    handleClick() {
      if (!this.allowClick) return

      if (this.name || this.desc || this.$slots.detail || this.$scopedSlots.detail) {
        // e.stopPropagation()
        this.$emit('click')
        this.showTooptip()
      }
    },
    showTooptip(e) {
      this.show = true
      this.lastTriggerEl = e && e.currentTarget ? e.currentTarget : this.$el
      this.tooltipAppended = false

      // 添加全局点击监听器
      this.$nextTick(() => {
        document.addEventListener('click', this.handleOutsideClick, { capture: true })
      })

      this.positionTooltip()
    },
    destroyTooltip() {
      this.show = false
      this.tooltipAppended = false

      // 清理 ResizeObserver
      if (this.resizeObserver) {
        this.resizeObserver.disconnect()
        this.resizeObserver = null
      }

      // 移除全局点击监听器
      document.removeEventListener('click', this.handleOutsideClick)
    },
    arrowLeftComputeEvent(needCompute, tooltip, rect) {
      // 如果做了位置调整，则重新计算箭头指向点击元素中间的偏移量
      if (needCompute) {
        // 获取当前 tooltip 的最新定位
        const newRect = tooltip.getBoundingClientRect()
        // 点击元素的中心点 X 坐标 (像素值)
        const centerX = rect.x + rect.width / 2
        // 箭头的 left 应该为：点击元素中心距离 tooltip 左边界的偏移量
        const arrowLeft = centerX - newRect.left
        // 设置 CSS 变量 --arrow-left 为具体像素值，比如："40px"
        tooltip.style.setProperty('--arrow-left', arrowLeft + 'px')
      }
    },
    arrowTopComputeEvent(needCompute, tooltip, rect) {
      if (needCompute) {
        const newRect = tooltip.getBoundingClientRect()
        const centerY = rect.y + rect.height / 2
        const arrowTop = centerY - newRect.top
        tooltip.style.setProperty('--arrow-top', arrowTop + 'px')
      }
    },
    positionTooltip() {
      this.$nextTick(() => {
        const tooltip = this.$refs.tooltip
        const trigger = this.lastTriggerEl || this.$el
        if (!tooltip || !trigger) return

        const container = this.getContainer()
        const dialog = trigger.closest && trigger.closest('.dialog-overlay')
        if (dialog) {
          const dialogZIndex = Number.parseInt(window.getComputedStyle(dialog).zIndex, 10)
          if (!Number.isNaN(dialogZIndex)) {
            tooltip.style.zIndex = dialogZIndex + 1
          }
        }
        // 先将 tooltip 添加到 DOM，确保后续计算能获取到准确尺寸
        const isFirstAppend = !this.tooltipAppended
        if (isFirstAppend) {
          container ? container.appendChild(tooltip) : this.$el.appendChild(tooltip)
          this.tooltipAppended = true
        }

        // 如果是首次添加到 DOM，需要再等一个 tick 让样式完全应用
        const doPosition = () => {
          const rect = trigger.getBoundingClientRect()
          const viewportOffsetTop = window.visualViewport ? window.visualViewport.offsetTop : 0
          const viewportOffsetLeft = window.visualViewport ? window.visualViewport.offsetLeft : 0
          const viewportHeight = window.visualViewport ? window.visualViewport.height : window.innerHeight
          const viewportWidth = window.visualViewport ? window.visualViewport.width : window.innerWidth
          // 由于 iOS13 fixed 参考 layout viewport，需要加上 visualViewport 偏移
          const topBase = rect.y + viewportOffsetTop
          const leftBase = rect.x + viewportOffsetLeft

          if (container) tooltip.style.position = 'fixed'
          else tooltip.style.position = 'absolute'
          // 清除所有方向类
          tooltip.classList.remove(
            'reward-tooltip--top',
            'reward-tooltip--bottom',
            'reward-tooltip--left',
            'reward-tooltip--right',
          )
          let needCompute = false
          // 添加当前方向类
          tooltip.classList.add(`reward-tooltip--${this.placement}`)
          tooltip.style.setProperty('--arrow-left', '50%')
          tooltip.style.setProperty('--arrow-top', '50%')
          // 根据 placement 设置提示框位置
          switch (this.placement) {
            case 'bottom':
              tooltip.style.top = topBase + rect.height + 'px'
              tooltip.style.left = leftBase + rect.width / 2 + 'px'
              tooltip.style.transform = 'translate3d(-50%, 0, 0)'
              this.$nextTick(() => {
                const tipRect = tooltip.getBoundingClientRect()
                // 若 tooltip 右侧越出屏幕，则向左移动
                if (tipRect.right > viewportWidth) {
                  tooltip.style.left = leftBase + rect.width + 'px'
                  tooltip.style.transform = 'translate3d(-100%, 0, 0)'
                  needCompute = true
                }
                // 如果 tooltip 左侧越出屏幕，则向右移动
                if (tipRect.left < 0) {
                  tooltip.style.left = leftBase + 'px'
                  tooltip.style.transform = 'translate3d(0, 0, 0)'
                  needCompute = true
                }
                // 如果 tooltip 底部超出屏幕，则改为上方显示
                if (tipRect.bottom > viewportHeight) {
                  tooltip.style.top = topBase + 'px'
                  tooltip.style.transform = 'translate3d(-50%, -100%, 0)'
                  tooltip.classList.remove('reward-tooltip--bottom')
                  tooltip.classList.add('reward-tooltip--top')

                  if (tipRect.right > viewportWidth) {
                    // 右侧越出屏幕且tooltip 顶部越出屏幕的情况处理
                    tooltip.style.transform = 'translate3d(-100%, -100%, 0)'
                  } else if (tipRect.left < 0) {
                    // 左侧越出屏幕且tooltip 顶部越出屏幕的情况处理
                    tooltip.style.transform = 'translate3d(0, -100%, 0)'
                  }
                }
                this.arrowLeftComputeEvent(needCompute, tooltip, rect)
              })
              break
            case 'left':
              tooltip.style.top = topBase + rect.height / 2 + 'px'
              tooltip.style.left = leftBase + 'px'
              tooltip.style.transform = 'translate3d(-100%, -50%, 0)'
              this.$nextTick(() => {
                const tipRect = tooltip.getBoundingClientRect()
                // 如果 tooltip 底部越出屏幕，则向上移动
                if (tipRect.bottom > viewportHeight) {
                  tooltip.style.top = topBase + rect.height + 'px'
                  tooltip.style.transform = 'translate3d(-100%, -100%, 0)'
                  needCompute = true
                }
                // 如果 tooltip 顶部越出屏幕，则向下移动
                if (tipRect.top < 0) {
                  tooltip.style.top = topBase + 'px'
                  tooltip.style.transform = 'translate3d(-100%, 0, 0)'
                  needCompute = true
                }
                if (tipRect.left < 0) {
                  tooltip.style.left = leftBase + rect.width + 'px'
                  tooltip.style.transform = 'translate3d(0, -50%, 0)'
                  // 清除所有方向类
                  tooltip.classList.remove('reward-tooltip--left')
                  tooltip.classList.add('reward-tooltip--right')

                  if (tipRect.bottom > viewportHeight) {
                    tooltip.style.transform = 'translate3d(0, -100%, 0)'
                  } else if (tipRect.top < 0) {
                    tooltip.style.transform = 'translate3d(0, 0, 0)'
                  }
                }
                // 如果做了位置调整，则重新计算箭头指向点击元素中间的偏移量
                this.arrowTopComputeEvent(needCompute, tooltip, rect)
              })
              break
            case 'right':
              tooltip.style.top = topBase + rect.height / 2 + 'px'
              tooltip.style.left = leftBase + rect.width + 'px'
              tooltip.style.transform = 'translate3d(0, -50%, 0)'
              this.$nextTick(() => {
                const tipRect = tooltip.getBoundingClientRect()
                // 如果 tooltip 底部越出屏幕，则向上移动
                if (tipRect.bottom > viewportHeight) {
                  tooltip.style.top = topBase + rect.height + 'px'
                  tooltip.style.transform = 'translate3d(0, -100%, 0)'
                  needCompute = true
                }
                // 如果 tooltip 顶部越出屏幕，则向下移动
                if (tipRect.top < 0) {
                  tooltip.style.top = topBase + 'px'
                  tooltip.style.transform = 'translate3d(0, 0, 0)'
                  needCompute = true
                }
                if (tipRect.right > viewportWidth) {
                  tooltip.style.left = leftBase + 'px'
                  tooltip.style.transform = 'translate3d(-100%, -50%, 0)'
                  // 清除所有方向类
                  tooltip.classList.remove('reward-tooltip--right')
                  tooltip.classList.add('reward-tooltip--left')

                  if (tipRect.bottom > viewportHeight) {
                    tooltip.style.transform = 'translate3d(-100%, -100%, 0)'
                  } else if (tipRect.top < 0) {
                    tooltip.style.transform = 'translate3d(-100%, 0, 0)'
                  }
                }
                // 如果做了位置调整，则重新计算箭头指向点击元素中间的偏移量
                this.arrowTopComputeEvent(needCompute, tooltip, rect)
              })
              break
            default: // top
              tooltip.style.top = topBase + 'px'
              tooltip.style.left = leftBase + rect.width / 2 + 'px'
              tooltip.style.transform = 'translate3d(-50%, -100%, 0)'
              this.$nextTick(() => {
                const tipRect = tooltip.getBoundingClientRect()
                // 若 tooltip 右侧越出屏幕，则向左移动
                if (tipRect.right > viewportWidth) {
                  tooltip.style.left = leftBase + rect.width + 'px'
                  tooltip.style.transform = 'translate3d(-100%, -100%, 0)'
                  needCompute = true
                  // 超出后，tooltip的箭头也需要保持在元素的上方中心
                }
                // 如果 tooltip 左侧越出屏幕，则向右移动
                if (tipRect.left < 0) {
                  tooltip.style.left = leftBase + 'px'
                  tooltip.style.transform = 'translate3d(0, -100%, 0)'
                  needCompute = true
                }
                // 若 tooltip 顶部越出屏幕，则切换到底部显示
                if (tipRect.top < 0) {
                  tooltip.style.top = topBase + rect.height + 'px'
                  tooltip.style.transform = 'translate3d(-50%, 0, 0)'
                  tooltip.classList.remove('reward-tooltip--top')
                  tooltip.classList.add('reward-tooltip--bottom')

                  if (tipRect.right > viewportWidth) {
                    // 右侧越出屏幕且tooltip 顶部越出屏幕的情况处理
                    tooltip.style.transform = 'translate3d(-100%, 0, 0)'
                  } else if (tipRect.left < 0) {
                    // 左侧越出屏幕且tooltip 顶部越出屏幕的情况处理
                    tooltip.style.transform = 'translate3d(0, 0, 0)'
                  }
                }
                this.arrowLeftComputeEvent(needCompute, tooltip, rect)
              })
          }
        }

        // 如果是首次添加到 DOM，需要等一个 tick；否则直接执行
        if (isFirstAppend) {
          this.$nextTick(doPosition)
        } else {
          doPosition()
        }

        // 使用 ResizeObserver 监听 tooltip 尺寸变化（图片加载、内容渲染）
        // 清理旧的 observer
        if (this.resizeObserver) {
          this.resizeObserver.disconnect()
        }

        // 创建新的 observer
        if (typeof ResizeObserver === 'undefined') return
        this.resizeObserver = new ResizeObserver(() => {
          // 尺寸变化时重新计算位置
          doPosition()
        })

        // 开始监听
        this.resizeObserver.observe(tooltip)
      })
    },
  },
}
</script>

<style>
  .reward-tooltip {
    position: relative;
  }

  .reward-tooltip__detail {
    --arrow-color: rgba(0, 0, 0, 90%);
    --arrow-size: 10px;
    z-index: 1;
    box-sizing: border-box;
    min-width: 160px;
    max-width: min(360px, calc(100vw - 24px));
    padding: 12px 16px;
    color: #fff;
    line-height: 1.5;
    background: var(--arrow-color);
    border-radius: 12px;
  }

  .reward-tooltip__detail::after {
    position: absolute;
    top: 100%;
    left: 50%;
    display: block;
    width: 0;
    height: 0;
    border: var(--arrow-size) solid transparent;
    content: '';
  }

  .reward-tooltip__name,
  .reward-tooltip__item-name {
    font-weight: 700;
  }

  .reward-tooltip__desc,
  .reward-tooltip__item-desc {
    margin-top: 4px;
    font-size: 12px;
  }

  .reward-tooltip__list {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  .reward-tooltip__item {
    display: flex;
    gap: 12px;
    align-items: center;
  }

  .reward-tooltip__item-img {
    position: relative;
    flex: 0 0 64px;
    width: 64px;
    height: 64px;
  }

  .reward-tooltip__item-img img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .reward-tooltip__item-img span {
    position: absolute;
    right: 2px;
    bottom: 0;
    color: #fff;
    font-weight: 700;
    text-shadow: 0 1px 2px #000;
  }

  .reward-tooltip__item-content {
    min-width: 0;
  }

  .reward-tooltip--top {
    margin-top: calc(var(--arrow-size) * -1);
  }

  .reward-tooltip--top::after {
    top: 100%;
    left: var(--arrow-left);
    margin-top: -1px;
    border-top-color: var(--arrow-color);
    transform: translateX(-50%);
  }

  .reward-tooltip--bottom {
    margin-top: var(--arrow-size);
  }

  .reward-tooltip--bottom::after {
    top: 0;
    left: var(--arrow-left);
    margin-top: 1px;
    border-bottom-color: var(--arrow-color);
    transform: translateX(-50%) translateY(-100%);
  }

  .reward-tooltip--left {
    margin-left: calc(var(--arrow-size) * -1);
  }

  .reward-tooltip--left::after {
    top: var(--arrow-top);
    left: 100%;
    margin-left: -1px;
    border-left-color: var(--arrow-color);
    transform: translateY(-50%);
  }

  .reward-tooltip--right {
    margin-left: var(--arrow-size);
  }

  .reward-tooltip--right::after {
    top: var(--arrow-top);
    left: 0;
    margin-left: 1px;
    border-right-color: var(--arrow-color);
    transform: translate(-100%, -50%);
  }
</style>
